import { Entity } from '@api';
import { ReadMore } from '@design/components/read-more/ReadMore';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OverviewKeyValues } from '../overview-key-values/OverviewKeyValues';
import styles from './About.module.scss';

export interface AboutProps {
  entity: Entity;
  isModal?: boolean;
  showMoreDescription?: boolean;
  variant?: 'default' | 'secondary';
  limit?: number;
}

export const About = ({
  entity,
  isModal = false,
  showMoreDescription = false,
  variant = 'default',
  limit = 200,
}: AboutProps) => {
  const { t } = useTranslation('widget');
  const isDefaultVariant = variant === 'default';

  /* i18next-extract-disable-next-line */
  const sectorName = t([
    `widget:assetOverview.companyProfile.sectorNames.${entity.gics.sector.code}`,
    'widget:assetOverview.companyProfile.sectorNames.notFound',
  ]);
  /* i18next-extract-disable-next-line */
  const industryName = t([
    `widget:assetOverview.companyProfile.sectorNames.${entity.gics.industry_group.code}`,
    'widget:assetOverview.companyProfile.sectorNames.notFound',
  ]);

  if (!entity.company_description) {
    return null;
  }

  return (
    <div
      className={clsx(styles.aboutRoot, {
        [styles.secondaryVariant]: !isDefaultVariant,
      })}
      data-testid="about"
    >
      <div className={styles.contentContainer}>
        <SectionTitle
          title={t('widget:assetOverview.companyProfile.about', {
            companyName: entity.name,
          })}
        />
        <div className={styles.sectorAndIndustryWrapper}>
          <div className={styles.subTitlesWrapper}>
            <span className={styles.label}>
              {t('widget:assetOverview.companyProfile.sector')}
            </span>
            <span className={styles.value}>{sectorName}</span>
          </div>
          <div className={styles.subTitlesWrapper}>
            <span className={styles.label}>
              {t('widget:assetOverview.companyProfile.subIndustry')}
            </span>
            <span className={styles.value}>{industryName}</span>
          </div>
        </div>
        <p
          className={clsx(styles.description, {
            [styles.isModal]: isModal,
            [styles.secondaryVariant]: !isDefaultVariant,
          })}
        >
          {showMoreDescription ? (
            <ReadMore limit={limit}>{entity.company_description}</ReadMore>
          ) : (
            entity.company_description
          )}
        </p>
        {!isDefaultVariant && (
          <OverviewKeyValues entityTag={entity.tag} isDefaultVariant={false} />
        )}
      </div>
    </div>
  );
};
